import type { VenueDetail } from '../../../../../api/venues';
import { photoUploadedBy } from '../booking-content-audit';

import { PhotoCardControls } from './PhotoCardControls';
import { PhotoCardStatus } from './PhotoCardStatus';
import { PhotoCropStep } from './PhotoCropStep';
import { PhotoGuidance } from './PhotoGuidance';
import { PhotoPreview } from './PhotoPreview';
import { PhotoProblemNotice } from './PhotoProblemNotice';
import { PhotoSectionLayout } from './PhotoSectionLayout';
import { PhotoSourceLine } from './PhotoSourceLine';
import { PhotoSpecs } from './PhotoSpecs';
import type { PhotoLayout } from './use-photo-layout';
import type { VenuePhotoFlow } from './use-venue-photo-flow';

export interface PhotoCardViewProps {
  venue: VenueDetail;
  readOnly: boolean;
  layout: PhotoLayout;
  flow: VenuePhotoFlow;
  guidanceId: string;
}

/** The booking photo card: at rest, while uploading or removing, or while a photo is being framed. */
export function PhotoCardView({ venue, readOnly, layout, flow, guidanceId }: PhotoCardViewProps) {
  const { phase, selection } = flow;
  const { url, source } = venue.bookingPhoto;
  const intro = (
    <PhotoGuidance
      id={guidanceId}
      hasPhoto={url !== null}
      cropping={phase === 'cropping' && !readOnly}
    />
  );

  if (phase === 'cropping' && selection && !readOnly) {
    return (
      <PhotoSectionLayout
        phone={layout.phone}
        leadWidth={layout.leadWidth}
        intro={intro}
        lead={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <PhotoProblemNotice flow={flow} layout={layout} />
            <PhotoCropStep
              selection={selection}
              flow={flow}
              layout={layout}
              guidanceId={guidanceId}
            />
          </div>
        }
      >
        <PhotoSpecs showDropHint={false} />
      </PhotoSectionLayout>
    );
  }

  return (
    <PhotoSectionLayout
      phone={layout.phone}
      leadWidth={layout.leadWidth}
      intro={intro}
      lead={
        <PhotoPreview
          src={url}
          venueName={venue.name}
          reading={phase === 'reading'}
          uploadProgress={phase === 'uploading' ? flow.progress : null}
          canDrop={!readOnly && phase === 'idle' && !layout.phone}
          onDropFile={(file) => void flow.choose(file)}
        />
      }
    >
      <div>
        <PhotoSourceLine
          source={source}
          who={source === 'staff' ? photoUploadedBy(venue.audit) : null}
        />
      </div>
      <PhotoCardStatus
        readOnly={readOnly}
        importedPhotoAvailable={venue.importedPhotoAvailable}
        layout={layout}
        flow={flow}
      />
      {!readOnly && <PhotoCardControls source={source} layout={layout} flow={flow} />}
    </PhotoSectionLayout>
  );
}
